import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import {
  SeamlessSurface,
  SeamlessCard,
  TypeChip,
  AssessmentPanel,
  AssessRow,
  NetScore,
  SectionLabel,
  ImplList,
  StatusButtons,
  SeamlessSkeleton,
  scoreBand,
} from './seamless/SeamlessKit';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { useAutosave } from './useAutosave';
import { type ProcedureResponseProps, computeResponseReadOnly } from './responseShellTypes';
import {
  transitionService,
  procedureInstanceService,
  controlTestResponseService,
  controlImplResponseService,
  findingService,
  procedureEvidenceService,
  type Finding,
  type ControlTestResponseRow,
  type ProcedureEvidence,
} from '../../../services/auditService';
import controlTestingService from '../../../services/controlTestingService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, { ITEM_TYPE_CONTROL } from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import RejectNoteCell from './RejectNoteCell';
import EvidenceCell from './EvidenceCell';

/**
 * Plan 05c-07 — sample-less ControlTest response body (STAFF-11).
 *
 * Mirrors RiskAssessmentResponse.tsx (the canonical Wave-3 sample-less analog),
 * with the per-row UI being Pass/Fail/NA radio + evidence_text textarea +
 * per-control evidence via the shared EvidenceCell + EvidenceModal surface
 * (Phase 113 — unified evidence model).
 *
 *   - One row per control attached to the procedure's template (from
 *     controlTestingService.listScope).
 *   - Autosaves via useAutosave (~1s debounce). First save on an un-saved
 *     row issues POST /adt/control_test_responses (lazy create — requires
 *     `result` to be non-empty since the backend NOT NULL); subsequent edits
 *     issue PUT.
 *   - Evidence: EvidenceCell (scope='row') per control row opens the shared
 *     EvidenceModal, uploads/lists/downloads/detaches via procedureEvidenceService.
 *     InlineQuestionMeta shows attached files + findings below each card.
 *
 * Finding-link mapping (CONTEXT D5):
 *   item_type_id  = ITEM_TYPE_CONTROL (11)
 *   item_record_id = control_id        (NOT response_id)
 * Rationale: a finding pins to the control the auditor tested, so it
 * survives even if the control_test_response row is later regenerated.
 *
 * Empty-scope behaviour: if the template has no attached controls, surface
 * an amber banner (mirrors RiskAssessmentResponse's scopeMissing flow) so
 * the auditor knows to ask their DRC admin to attach controls via the
 * Procedure Templates → ControlTest editor.
 */

interface CTRow {
  /** Stable React key — "ctrl-{control_id}". */
  key: string;
  /** CTR_controls.id; FK target of control_test_responses.control_id + finding item_record_id. */
  control_id: number;
  /** Display only. */
  control_code?: string | null;
  /** Display only. */
  control_name?: string | null;
  /** Display only. */
  control_type?: string | null;
  /** Display only. */
  category_name?: string | null;
  /** Editable. '' = user has not yet picked a radio; treated as not-yet-savable for CREATE. */
  result: 'Pass' | 'Fail' | 'NA' | '';
  /** Editable. */
  evidence_text: string;
  /** Server-assigned UTL_files.id; null until the user uploads a file. */
  evidence_file_id: number | null;
  /** Display only; populated post-upload or by initial load (if backend LEFT-JOINs filename). */
  evidence_filename?: string | null;
  /** Local UI state — true while an upload is in flight. */
  uploading: boolean;
  /** Server-assigned after first save. */
  response_id?: number;
  /** Parity: per-control Existence + Effectiveness scores (1-5). '' = unset. */
  existence_score: number | '';
  effectiveness_score: number | '';
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

/** Parity model B — one implementation row under a control (definition from CTR
 *  + this audit's saved Pass/Fail/NA + observations). */
interface ImplRow {
  key: string;                 // "impl-{implementation_id}"
  control_id: number;
  implementation_id: number;
  sequence_number?: number | null;
  label: string;               // explanation (the implementation description)
  implementation_type?: string | null;
  response_id?: number | null; // adt_control_implementation_responses.id
  result: 'Pass' | 'Fail' | 'NA' | '';
  observations: string;
  dirty: boolean;
}

/**
 * RemarksModal — per-row note/observation editor (parity with ChecklistResponse).
 * Edits a LOCAL draft and commits once on Done, so autosave isn't woken on every
 * keystroke.
 */
const RemarksModal: React.FC<{
  title: string;
  heading: string;
  initialValue: string;
  readOnly: boolean;
  placeholder?: string;
  testId?: string;
  onCommit: (value: string) => void;
  onClose: () => void;
}> = ({ title, heading, initialValue, readOnly, placeholder, testId, onCommit, onClose }) => {
  const [draft, setDraft] = useState(initialValue);
  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>{`Remarks under "${title}"`}</Modal.Header>
      <Modal.Body>
        <FormControl.Textarea
          label=""
          data-testid={testId}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          rows={5}
          placeholder={placeholder}
          autoFocus
          disabled={readOnly}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="fill" onClick={() => { onCommit(draft); onClose(); }} disabled={readOnly}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ControlTestResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // G3 — an Approved instance is immutable. Derive from workflow_state unless the
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const [rows, setRows] = useState<CTRow[]>([]);
  const [impls, setImpls] = useState<ImplRow[]>([]);
  const [scopeMissing, setScopeMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Collapse-section open/closed state — keyed by row.key. Controls default to
  // open (the auditor tests each one) but can be collapsed via the chevron.
  // Cards are always open (no expand/collapse).

  // Plan 05b-05 / STAFF-06 — inline finding raise state. Finding link target
  // is control_id (CONTEXT D5), so modalRow carries controlId, not response_id.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    controlId: number;
  } | null>(null);
  // Remarks/observation popup (evidence note on a control OR an implementation
  // observation). Carries the commit callback so one modal serves both.
  const [remarks, setRemarks] = useState<
    { heading: string; title: string; value: string; placeholder?: string; testId?: string; onCommit: (v: string) => void } | null
  >(null);

  // Phase 113 REQ-8 — per-control evidence, fetched once and grouped by control_id.
  // ControlTest evidence is keyed by (ITEM_TYPE_CONTROL, control_id) per CONTEXT D5.
  const [evidenceByControl, setEvidenceByControl] = useState<Map<number, ProcedureEvidence[]>>(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<number, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id === ITEM_TYPE_CONTROL && ev.item_record_id != null) {
          const bucket = map.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          map.set(ev.item_record_id, bucket);
        }
      }
      setEvidenceByControl(map);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForControl = useCallback(
    (controlId: number): Finding[] =>
      findings.filter(
        (f) =>
          f.item_type_id === ITEM_TYPE_CONTROL &&
          f.item_record_id === controlId,
      ),
    [findings],
  );

  // Initial load — fetch procedure_template_id from the instance, then the
  // CT scope (one row per attached control), then any existing
  // control_test_responses, then merge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;

        let scope: Array<{
          control_id: number;
          control_code?: string | null;
          control_name?: string | null;
          control_type?: string | null;
          category_name?: string | null;
        }> = [];
        if (templateId) {
          try {
            const items = await controlTestingService.listScope(templateId);
            // Defensive shape normalisation — service already returns an
            // array, but tolerate `{data: []}` envelope just in case.
            const arr: any[] = Array.isArray(items) ? items : ((items as any)?.data ?? []);
            scope = arr
              .map((row: any) => ({
                control_id: row.control_id ?? row.id ?? row.control?.id,
                control_code: row.control_code ?? row.control?.control_code ?? row.code ?? null,
                control_name: row.control_name ?? row.control?.control_name ?? row.name ?? null,
                control_type: row.control_type ?? row.control?.control_type ?? null,
                category_name: row.category_name ?? row.control?.category_name ?? null,
              }))
              .filter((r: any) => r.control_id != null);
          } catch {
            // Scope fetch failed — fall through to empty scope.
          }
        }

        if (scope.length === 0) {
          if (!cancelled) {
            setScopeMissing(true);
            setRows([]);
          }
          return;
        }

        const responses: ControlTestResponseRow[] =
          await controlTestResponseService.list(instance.id);
        const byControlId = new Map<number, ControlTestResponseRow>();
        for (const r of responses) byControlId.set(r.control_id, r);

        const merged: CTRow[] = scope.map((s) => {
          const existing = byControlId.get(s.control_id);
          return {
            key: `ctrl-${s.control_id}`,
            control_id: s.control_id,
            control_code: s.control_code ?? null,
            control_name: s.control_name ?? null,
            control_type: s.control_type ?? null,
            category_name: s.category_name ?? null,
            result: (existing?.result as CTRow['result']) ?? '',
            evidence_text: existing?.evidence_text ?? '',
            evidence_file_id: existing?.evidence_file_id ?? null,
            // Backend may LEFT-JOIN filename onto the response row; tolerate either way.
            evidence_filename: (existing as any)?.evidence_filename ?? null,
            uploading: false,
            response_id: existing?.id,
            existence_score: (existing?.existence_score ?? '') as number | '',
            effectiveness_score: (existing?.effectiveness_score ?? '') as number | '',
            reject_note: (existing as any)?.reject_note ?? '',
            dirty: false,
          };
        });

        // Parity model B — implementation rows (definitions from CTR + saved results).
        let mappedImpls: ImplRow[] = [];
        try {
          const implRows = await controlImplResponseService.list(instance.id);
          mappedImpls = implRows.map((ir) => ({
            key: `impl-${ir.implementation_id}`,
            control_id: ir.control_id,
            implementation_id: ir.implementation_id,
            sequence_number: ir.sequence_number ?? null,
            label: ir.explanation || `Implementation ${ir.implementation_id}`,
            implementation_type: ir.implementation_type ?? null,
            response_id: ir.response_id ?? null,
            result: (ir.result as ImplRow['result']) ?? '',
            observations: ir.observations ?? '',
            dirty: false,
          }));
        } catch {
          // Non-fatal — implementation rows just won't show.
        }

        if (!cancelled) {
          setRows(merged);
          setImpls(mappedImpls);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load control-test scope.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Debounced save:
  //   * On UPDATE: send whichever fields the user has touched; the backend
  //     accepts partial updates for result / evidence_text / evidence_file_id.
  //   * On CREATE: require `result` to be non-empty so the row is meaningful
  //     (result is NOT NULL in the DB) — an evidence-only row stays local
  //     until the auditor picks a radio.
  const save = useCallback(async () => {
    // 1. Control-level rows — result + evidence + Existence/Effectiveness scores.
    //    Skip rows with an outstanding evidence-text validation error (origin/dev).
    const dirtyRows = rows.filter((r) => r.dirty && !obsErrors[r.key]);
    const rowUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirtyRows) {
      const hasScore = r.existence_score !== '' || r.effectiveness_score !== '';
      const ex = r.existence_score === '' ? null : r.existence_score;
      const ef = r.effectiveness_score === '' ? null : r.effectiveness_score;
      if (r.response_id) {
        await controlTestResponseService.update(r.response_id, {
          result: r.result === '' ? null : (r.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: r.evidence_text || null,
          evidence_file_id: r.evidence_file_id,
          existence_score: ex,
          effectiveness_score: ef,
          modified_by: currentUserId,
        });
        rowUpdates.push({ key: r.key, response_id: r.response_id });
      } else if (r.result !== '' || hasScore) {
        const created = await controlTestResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          control_id: r.control_id,
          result: r.result === '' ? null : (r.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: r.evidence_text || null,
          evidence_file_id: r.evidence_file_id,
          existence_score: ex,
          effectiveness_score: ef,
          created_by: currentUserId,
        });
        rowUpdates.push({ key: r.key, response_id: created?.id });
      }
    }

    // 2. Implementation rows — per-implementation Pass/Fail/NA + observations.
    const dirtyImpls = impls.filter((i) => i.dirty);
    const implUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const i of dirtyImpls) {
      if (i.response_id) {
        await controlImplResponseService.update(i.response_id, {
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: i.response_id });
      } else if (i.result !== '' || i.observations.trim() !== '') {
        const created = await controlImplResponseService.create({
          procedure_instance_id: instance.id,
          control_id: i.control_id,
          implementation_id: i.implementation_id,
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: created?.id });
      }
    }

    if (rowUpdates.length) {
      setRows((prev) =>
        prev.map((r) => {
          const u = rowUpdates.find((x) => x.key === r.key);
          return u ? { ...r, response_id: u.response_id ?? r.response_id, dirty: false } : r;
        }),
      );
    }
    if (implUpdates.length) {
      setImpls((prev) =>
        prev.map((i) => {
          const u = implUpdates.find((x) => x.key === i.key);
          return u ? { ...i, response_id: u.response_id ?? i.response_id, dirty: false } : i;
        }),
      );
    }
  }, [rows, impls, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateRow = (idx: number, patch: Partial<CTRow>) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, dirty: true };
      return next;
    });
    markDirty();
  };

  // Update an implementation row by its key (impls is a flat cross-control list).
  const updateImpl = (key: string, patch: Partial<ImplRow>) => {
    setImpls((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch, dirty: true } : i)));
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const result = validate(110, value);
    if (!result.valid) setObsErrors(prev => ({ ...prev, [rowKey]: result.message ?? 'Invalid evidence text.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // G2 — number of controls a checker has rejected (drives the shell approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every control must carry a Pass/Fail/NA result
  // before submit. '' = not yet picked.
  const unanswered = rows.filter(r => r.result === '').length;
  const incompleteReason =
    rows.length > 0 && unanswered > 0
      ? `Mark a result for every control before submitting — ${unanswered} of ${rows.length} still blank.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="ControlTest"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="inline-error inline-error--inline" role="alert">{loadError}</div>
      )}
      {scopeMissing && (
        <div className="warning-banner" role="status">
          No controls attached to this procedure template. Ask your DRC admin to
          attach controls via Procedure Templates &rarr; ControlTest editor.
        </div>
      )}
      {rows.length > 0 && (
        <SeamlessSurface className="seamless-tight">
          {rows.map((r, idx) => {
            const rowFindings = findingsForControl(r.control_id);
            // Net Score = Existence × Effectiveness (out of 25) — mirrors the GRC modal.
            const implsForControl = impls.filter((i) => i.control_id === r.control_id);
            // Unset sliders count as 0 — the score always shows (0/25 when zero),
            // never "Not scored".
            const netScore =
              (r.existence_score === '' ? 0 : (r.existence_score as number)) *
              (r.effectiveness_score === '' ? 0 : (r.effectiveness_score as number));
            const variant = scoreBand(netScore);
            return (
              <SeamlessCard
                key={r.key}
                code={r.control_code}
                sno={idx + 1}
                snoLabel={`C${idx + 1}`}
                variant="control"
                title={r.control_name ?? 'Unknown control'}
                typeChip={<TypeChip label={r.control_type} />}
                assessment={
                  <AssessmentPanel
                    verdict={
                      <NetScore
                        text={`${netScore}/25`}
                        variant={variant}
                        title="Net Score = Existence × Effectiveness (out of 25)"
                        testId={`LBL_control_test_net_score_${r.control_id}`}
                      />
                    }
                  >
                    {/*
                      The control-level verdict. `result` is what the submit gate
                      below counts, what the save sends, and what the checker
                      reviews — but nothing in this screen ever set it, so the
                      blocked message read "N of N still blank" however much the
                      maker filled in, and the procedure could not be submitted
                      through the interface at all. Same component and vocabulary
                      as the implementation rows beneath.
                    */}
                    <div className="collapse-section__result-row" style={{ marginBottom: 8 }}>
                      <SectionLabel>Result</SectionLabel>
                      <StatusButtons
                        options={[
                          { val: 'Pass', label: 'Pass', mod: 'pass' },
                          { val: 'Fail', label: 'Fail', mod: 'fail' },
                          { val: 'NA', label: 'NA', mod: 'na' },
                        ]}
                        value={r.result}
                        disabled={readOnly}
                        onChange={(v) => updateRow(idx, { result: v as CTRow['result'] })}
                        testId={(v) =>
                          `BTN_control_test_result_${r.control_id}_${v.toLowerCase()}`}
                      />
                    </div>
                    <AssessRow
                      label="Ex"
                      labelTitle="Existence"
                      value={r.existence_score}
                      disabled={readOnly}
                      onChange={(v) => updateRow(idx, { existence_score: v })}
                      testId={`SEL_control_test_existence_${r.control_id}`}
                    />
                    <AssessRow
                      label="Ef"
                      labelTitle="Effectiveness"
                      value={r.effectiveness_score}
                      disabled={readOnly}
                      onChange={(v) => updateRow(idx, { effectiveness_score: v })}
                      testId={`SEL_control_test_effectiveness_${r.control_id}`}
                    />
                  </AssessmentPanel>
                }
                inlineRemark={(() => {
                  const ctrlEvidence = evidenceByControl.get(r.control_id) ?? [];
                  const ctrlFindings = findingsForControl(r.control_id);
                  return (
                    <InlineQuestionMeta
                      remark={r.evidence_text}
                      evidence={ctrlEvidence}
                      findings={ctrlFindings}
                      readOnly={readOnly}
                      onEvidenceRemoved={reloadEvidence}
                      onRemarkClick={readOnly ? undefined : () =>
                        setRemarks({
                          heading: 'Evidence note',
                          title: r.control_name ?? 'Control',
                          value: r.evidence_text,
                          placeholder: 'Note the evidence you reviewed (optional)',
                          testId: `INP_control_test_evidence_text_${r.key}`,
                          onCommit: (v) => {
                            setObsErrors((prev) => { const n = { ...prev }; delete n[r.key]; return n; });
                            updateRow(idx, { evidence_text: v });
                            handleObsBlur(r.key, v);
                          },
                        })
                      }
                    />
                  );
                })()}
                impl={
                  implsForControl.length > 0 ? (
                    <>
                      <SectionLabel>Implementations</SectionLabel>
                      <ImplList
                        items={implsForControl.map((im) => ({
                          id: im.implementation_id,
                          sno: im.sequence_number,
                          title: im.label,
                          sub: im.implementation_type,
                          result: im.result,
                          observations: im.observations,
                        }))}
                        onResult={(id, result) => updateImpl(`impl-${id}`, { result: result as ImplRow['result'] })}
                        onObservations={(id, text) => updateImpl(`impl-${id}`, { observations: text })}
                        onOpenObservation={(id) => {
                          const im = implsForControl.find((x) => x.implementation_id === id);
                          setRemarks({
                            heading: 'Observation',
                            title: im?.label ?? 'Implementation',
                            value: im?.observations ?? '',
                            placeholder: 'Observation (optional)',
                            testId: `INP_control_impl_obs_${id}`,
                            onCommit: (v) => updateImpl(`impl-${id}`, { observations: v }),
                          });
                        }}
                        disabled={readOnly}
                        testIdPrefix="control_impl"
                      />
                    </>
                  ) : undefined
                }

                inlineActions={
                  <div className="response-action-cluster response-action-cluster--inline">
                    <button
                      type="button"
                      className="response-row-action"
                      aria-label="Add remark"
                      title={r.evidence_text ? 'Edit evidence note' : 'Add evidence note'}
                      data-testid={`BTN_control_test_note_${r.control_id}`}
                      onClick={() =>
                        setRemarks({
                          heading: 'Evidence note',
                          title: r.control_name ?? 'Control',
                          value: r.evidence_text,
                          placeholder: 'Note the evidence you reviewed (optional)',
                          testId: `INP_control_test_evidence_text_${r.key}`,
                          onCommit: (v) => {
                            setObsErrors((prev) => { const n = { ...prev }; delete n[r.key]; return n; });
                            updateRow(idx, { evidence_text: v });
                            handleObsBlur(r.key, v);
                          },
                        })
                      }
                      disabled={readOnly}
                    >
                      <MessageSquare size={14} />
                    </button>
                    <EvidenceCell
                      scope="row"
                      entityId={entityId}
                      createdBy={currentUserId}
                      disabled={readOnly}
                      procedureInstanceId={instance.id}
                      itemTypeId={ITEM_TYPE_CONTROL}
                      itemRecordId={r.control_id}
                      questionLabel={r.control_name}
                      showCount={false}
                      onChanged={reloadEvidence}
                    />
                    <RaiseFindingButton
                      count={rowFindings.length}
                      showCount={false}
                      disabled={readOnly}
                      title="Raise a deficiency"
                      data-testid={`BTN_control_test_finding_${r.control_id}`}
                      onClick={() => {
                        setModalRow({
                          context: {
                            procedureName: (instance as any).audit_procedure_name,
                            sampleLabel: r.control_name ?? 'Unknown control',
                            responseValue: r.evidence_text || null,
                          },
                          controlId: r.control_id,
                        });
                      }}
                    />
                  </div>
                }
                footer={
                  <RejectNoteCell
                    responseId={r.response_id}
                    rejectNote={r.reject_note}
                    procedureType="ControlTest"
                    role={role}
                    readOnly={readOnly}
                    onSaved={(note) =>
                      setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, reject_note: note } : x)))
                    }
                  />
                }
              />
            );
          })}
        </SeamlessSurface>
      )}
      {rows.length === 0 && !loadError && !scopeMissing && <SeamlessSkeleton />}

      {/* Inline finding raise — polymorphic link is control_id (D5),
          NOT response_id. */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: (instance as any).audit_procedure_name }}
        itemTypeId={ITEM_TYPE_CONTROL}
        itemRecordId={modalRow?.controlId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
      {/* Remarks / observation popup (evidence note + implementation observation). */}
      {remarks && (
        <RemarksModal
          heading={remarks.heading}
          title={remarks.title}
          initialValue={remarks.value}
          placeholder={remarks.placeholder}
          testId={remarks.testId}
          readOnly={readOnly}
          onCommit={remarks.onCommit}
          onClose={() => setRemarks(null)}
        />
      )}
    </ResponseShell>
  );
};

export default ControlTestResponse;
